import PropTypes from "prop-types";
import React from "react";
import noop from "lodash/fp/noop";
import omit from "lodash/fp/omit";
import Close from "./icons/Close";
import { primary, darkGray, orange, text, lightestGray, lightGray, sealBlue, red } from "@govlaunch/palette";

/**
 * @param  {Boolean} active
 * @param  {Boolean} selected
 * @return {?Object}
 */
function applyActiveStyles({ active, selected, highlighted }) {
  if (highlighted) {
    return {
      borderColor: orange,
    };
  }

  if (active || selected) {
    return {
      color: text,
      borderColor: primary,
    };
  }

  return null;
}

/**
 * @return {Object}
 * @return {Object}
 */
function applyDisabledStyles(props) {
  if (props.disabled) {
    return {
      cursor: "default",
      pointerEvents: "none",
      backgroundColor: lightestGray,
      borderColor: lightGray,
      color: sealBlue,
    };
  }

  return null;
}

/**
 * @return {Object}
 * @return {Object}
 */
function applyValidationStateStyles(props) {
  if (props.validationState === "error") {
    return {
      color: red,
      borderColor: red,
    };
  }

  return null;
}

export default function Tag({ children, css, ...props }) {
  return (
    <div
      css={{
        maxWidth: 200,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",

        overflow: "hidden",
        textOverflow: "ellipsis",
        cursor: "pointer",
        border: `solid 1px ${lightGray}`,
        borderRadius: 4,
        color: darkGray,

        fontSize: 12,
        fontWeight: 500,
        lineHeight: 2.33,
        height: 25,
        marginRight: 10,
        marginTop: 5,
        padding: 7,

        backgroundColor: "#ffffff",

        ...applyActiveStyles(props),
        ...applyDisabledStyles(props),
        ...applyValidationStateStyles(props),
        ...css,
      }}
      {...omit(["active", "isActive", "validationState"], props)}
    >
      {children}
    </div>
  );
}

const sanitizeTitle = children =>
  Array.isArray(children) ? children.filter(child => typeof child === "string").join("") : children;

export function TagText({ children, css, ...props }) {
  return (
    <span
      title={sanitizeTitle(children)}
      css={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        position: "relative",
        ...css,
      }}
      {...props}
    >
      {children}
    </span>
  );
}

TagText.propTypes = {
  children: PropTypes.any.isRequired,
  css: PropTypes.object,
};

TagText.defaultProps = {
  css: {},
};

export function TagClose({ css, ...props }) {
  return (
    <div
      role="button"
      aria-label="remove item"
      css={{
        color: primary,
        ...css,
      }}
      {...props}
    >
      <Close width={7} height={7} />
    </div>
  );
}

TagClose.propTypes = {
  css: PropTypes.object,
};

TagClose.defaultProps = {
  css: {},
};

Tag.propTypes = {
  children: PropTypes.any.isRequired,
  onClose: PropTypes.func,
  active: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  selected: PropTypes.bool,
  validationState: PropTypes.oneOf(["error"]),
  css: PropTypes.object,
};

Tag.defaultProps = {
  onClose: noop,
  active: false,
  selected: false,
  validationState: null,
  css: null,
};
